import { ValidationLevel } from 'ui';
import isNumber from 'lodash/isnumber';
import { getCustomValue } from '../filters/get-custom-value';
import {
  FilterMenuValues, Filter, FilterValidation, PredefinedIds, FilterType,
  SubOptionType, OptionType, FilterCommonValues
} from '../filters/types';
import { getFieldId } from '../filters/selectors';
import { FilterPayloadValues } from '../filters/types';
import { allPredefinedFilters, filterId, fieldType } from '../constants';
import {
  DateRangeObject, DecimalRangeObject, FilterCommonTypes, FilterSearchModel, MenuValuesObject,
  PredefinedFilterObject, RangeObject, SelectedIdsObject, SerializeCodes, DefaultValueObject
} from '../types-store';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

type SelectedIdSerialization = (value: FilterCommonValues<FilterCommonTypes>[], menuValues: FilterMenuValues) => SelectedIdsObject;
type RangeSerialization = (range: FilterCommonValues<FilterCommonTypes>, menuValues: FilterMenuValues) => RangeObject;
type DateRangeSerialization = (range: any, menuValues: FilterMenuValues, filter?: Filter) => DateRangeObject;
type DecimalRangeSerialization = (range: any, menuValues: FilterMenuValues, filter?: Filter) => DecimalRangeObject;
type DefaultValueSerialization = (name: string, value: FilterCommonValues<FilterCommonTypes>[]) => DefaultValueObject;
type SerializeFilter = {
  [key: string]: (
    formValues: FilterPayloadValues<FilterCommonTypes>,
    menuValues: { [key: string]: FilterMenuValues },
    filter: Filter,
    result: FilterSearchModel,
    filterValidation: FilterValidation[]
  ) => FilterSearchModel
};

export const serializePredefined = (
  formValues: FilterPayloadValues<FilterCommonTypes>
): { [key: string]: PredefinedFilterObject | boolean } => {

  const include = formValues && formValues[PredefinedIds.include] || [];
  const exclude = formValues && formValues[PredefinedIds.exclude] || [];

  return {
    ...(include.some(i => i.value === allPredefinedFilters.inactiveUsers) && {
      [filterId.excludeInactiveUsers]: { enabled: true, negated: true }
    }),
    ...(exclude.some(i => i.value === allPredefinedFilters.inactiveUsers) && {
      [filterId.excludeInactiveUsers]: { enabled: true, negated: false }
    }),

    ...(include.some(i => i.value === allPredefinedFilters.technicalUsers) && {
      [filterId.excludeTechnicalUsers]: { enabled: true, negated: true }
    }),
    ...(exclude.some(i => i.value === allPredefinedFilters.technicalUsers) && {
      [filterId.excludeTechnicalUsers]: { enabled: true, negated: false }
    }),

    ...(include.some(i => i.value === allPredefinedFilters.withoutEmailAddress) && {
      [filterId.excludeUsersWithEmail]: { enabled: true, negated: false }
    }),
    ...(exclude.some(i => i.value === allPredefinedFilters.withoutEmailAddress) && {
      [filterId.excludeUsersWithEmail]: { enabled: true, negated: true }
    }),

    ...(include.some(i => i.value === allPredefinedFilters.withoutAlternativeID) && {
      [filterId.usersWithAlternativeId]: { enabled: true, negated: true }
    }),
    ...(exclude.some(i => i.value === allPredefinedFilters.withoutAlternativeID) && {
      [filterId.usersWithAlternativeId]: { enabled: true, negated: false }
    }),

    ...(include.some(i => i.value === allPredefinedFilters.administratedByMe) && {
      [filterId.usersFromGroupsAdministratedByMe]: { enabled: true, negated: false }
    }),
    ...(exclude.some(i => i.value === allPredefinedFilters.administratedByMe) && {
      [filterId.usersFromGroupsAdministratedByMe]: { enabled: true, negated: true }
    }),

    ...(include.some(i => i.value === allPredefinedFilters.excludeInactiveCodes) && {
      [filterId.includeInactiveCodes]: { enabled: false }
    }),

    ...(include.some(i => i.value === allPredefinedFilters.hideHistoricalFactors) && {
      [filterId.hideHistoricalFactors]: true
    })
  };
};

const serializeApplyRoleAccess = (filter: Filter, formValues: FilterPayloadValues<FilterCommonTypes>) => {
  const excludeInheritedId = getFieldId(filter.id, fieldType.excludeInherited);

  return filter.fields.some(f => f.fieldTypeId === fieldType.excludeInherited)
    ? { applyRoleAccess: formValues[excludeInheritedId]?.length > 0 }
    : {};
};

const serializeMenuValues = (menuValues: FilterMenuValues): MenuValuesObject => ({
  ...(menuValues && menuValues.exclude ? { negated: true } : {}),
  ...(menuValues && (menuValues.excludeChildren || menuValues.excludeParents) ? { ignoreHierarchy: true } : {})
});

const serializeDefaultValueFilter: DefaultValueSerialization = (name, value) => ({
  [name]: value.map(v => ({ v: v.value }))
});

const serializeDefaultValueFilterItem: DefaultValueSerialization = (name, value) => ({
  [name]: value.map(v => ({ v: v.value }))?.[0]
});

const serializeCodeFilter: SelectedIdSerialization = (value, menuValues) => ({
  selectedIds: value.map(v => ({ v: v.value })),
  ...serializeMenuValues(menuValues)
});

const serializeSingleValue: RangeSerialization = (range, menuValues) => ({
  text: isNumber(range.value) ? range.value : removeLastComma(String(range.value).replace(/\s/g, '')),
  ...serializeMenuValues(menuValues)
});

const serializeTextValue: RangeSerialization = (text, menuValues) => ({
  text: text.value.toString().trim(),
  ...serializeMenuValues(menuValues)
});

const serializeDateRangeValue: DateRangeSerialization = (range, menuValues, filter?: Filter) => {
  const dateFrom = range.find(f => f.subOptionType === SubOptionType.dateFrom);
  const dateTo = range.find(f => f.subOptionType === SubOptionType.dateTo);
  const notSpecified = range.some(f => f.optionType === OptionType.notSpecified);

  return {
    ...((!!dateFrom || !!dateTo) && {
      dateTimeRange: {
        ...(dateFrom && dateFrom.value && { from: dateFrom.value.toISOString() }),
        ...(dateTo && dateTo.value && { to: getDateTo(dateTo, filter) }),
        ...(menuValues && menuValues.exclude ? { negated: true } : {})
      }
    }),
    ...(notSpecified && {
      excludeNullValues: {
        enabled: true,
        ...(menuValues && menuValues.exclude ? {} : { negated: true })
      }
    })
  };
};

const serializeDecimalRangeValue: DecimalRangeSerialization = (range, menuValues, filter?: Filter) => {
  const rangeFrom = range.find(f => f.subOptionType === SubOptionType.decimalFrom);
  const rangeTo = range.find(f => f.subOptionType === SubOptionType.decimalTo);

  const isRangeFrom = isDefinedIncludingZero(rangeFrom?.value);
  const isRangeTo = isDefinedIncludingZero(rangeTo?.value);

  return {
    ...((isRangeFrom || isRangeTo) && {
      ...(isRangeFrom && { from: rangeFrom.value }),
      ...(isRangeTo && { to: rangeTo.value }),
      ...(menuValues && menuValues.exclude ? { negated: true } : {})
    })
  };
};

const getDateTo = (dateTo: FilterCommonValues<Date>, filter?: Filter) => dateTo && dateTo.value ?
  (filter?.options?.dateToAsEndOfDay ? mapDateToEndOfDay(dateTo.value)?.toISOString() : dateTo.value.toISOString())
  : undefined;

const mapDateToEndOfDay = (date: Date) => {
  const end = new Date(date.getTime());
  end.setUTCHours(23, 59, 59, 999);
  return end;
};

const removeLastComma = (value: string) => value && value[value.length - 1] === ',' ? value.slice(0, value.length - 1) : value;

const serializeSelectedIds = (
  formValues: FilterPayloadValues<FilterCommonTypes>,
  menuValues: { [key: string]: FilterMenuValues },
  filter: Filter,
  result: FilterSearchModel): { [key: string]: SelectedIdsObject } => {
  const rootId = getFieldId(filter.id, fieldType.root);
  const rootHasValues = formValues[rootId] && formValues[rootId].length > 0;
  const applicationId = getFieldId(filter.id, fieldType.application);
  const applicationHasValues = formValues[applicationId] && formValues[applicationId].length > 0;
  const negated = filter.fields.some(f => !!f.negated) ? { negated: true } : {};

  return rootHasValues || applicationHasValues
    ? {
      ...result,
      [filter.id]: {
        ...(rootHasValues
          ? serializeCodeFilter(formValues[rootId], menuValues && menuValues[rootId])
          : {}),

        ...serializeApplyRoleAccess(filter, formValues),

        ...negated,
        ...(applicationHasValues ? {
          applicationFilter: serializeCodeFilter(formValues[applicationId], menuValues && menuValues[applicationId])
        } : {})
      }
    }
    : result;
};

const serializeCustom = (
  formValues: FilterPayloadValues<FilterCommonTypes>,
  menuValues: { [key: string]: FilterMenuValues },
  filter: Filter,
  result: FilterSearchModel
): { [key: string]: RangeObject } => {

  const fieldValues = filter.fields.reduce((values, field) => {

    const id = getFieldId(filter.id, field.fieldTypeId);

    const value = getCustomValue(formValues[id], field.type);

    if (value === undefined) {
      return values;
    }

    return {
      ...values,
      [field.fieldTypeId]: value,
      ...serializeMenuValues(menuValues && menuValues[id])
    };
  }, {});

  const filterHasValues = Object.keys(fieldValues).length > 0;

  return filterHasValues
    ? {
      ...result,
      [filter.id]: fieldValues
    }
    : result;
};

const serializeDefaultValue = (
  formValues: FilterPayloadValues<FilterCommonTypes>,
  menuValues: { [key: string]: FilterMenuValues },
  filter: Filter,
  result: FilterSearchModel): { [key: string]: SelectedIdsObject } => {

  const codetableId = getFieldId(filter.id, fieldType.codetable);
  const codetableHasValues = formValues[codetableId] && formValues[codetableId].length > 0;

  const domainId = getFieldId(filter.id, fieldType.domains);
  const domainHasValues = formValues[domainId] && formValues[domainId].length > 0;

  const defaultValueId = getFieldId(filter.id, fieldType.defaultValues);
  const defaultValueHasValues = formValues[defaultValueId] && formValues[defaultValueId].length > 0;

  const applicationId = getFieldId(filter.id, fieldType.application);
  const applicationHasValues = formValues[applicationId] && formValues[applicationId].length > 0;

  return codetableHasValues || domainHasValues || defaultValueHasValues || applicationHasValues
    ? {
      ...result,
      [filter.id]: {
        ...(codetableHasValues
          ? serializeDefaultValueFilterItem(fieldType.codetable, formValues[codetableId])
          : {}),

        ...(applicationHasValues
          ? serializeDefaultValueFilterItem(fieldType.application, formValues[applicationId])
          : {}),

        ...(defaultValueHasValues
          ? serializeDefaultValueFilter(fieldType.defaultValues, formValues[defaultValueId])
          : {}),

        ...(domainHasValues
          ? serializeDefaultValueFilter(fieldType.domains, formValues[domainId])
          : {}),

        ...serializeApplyRoleAccess(filter, formValues)
      }
    }
    : result;
};

const serializeText = (
  formValues: FilterPayloadValues<FilterCommonTypes>,
  menuValues: { [key: string]: FilterMenuValues },
  filter: Filter,
  result: FilterSearchModel): { [key: string]: RangeObject } => {

  const rootId = getFieldId(filter.id, fieldType.root);
  const rootHasValues = !!formValues[rootId];
  const text = rootHasValues && formValues[rootId][0];

  if (!text) {
    return result;
  }

  const serializedText: RangeObject = serializeTextValue(text, menuValues && menuValues[rootId]);

  return !!serializedText
    ? {
      ...result,
      [filter.id]: {
        ...serializeApplyRoleAccess(filter, formValues),
        ...serializedText
      }
    }
    : result;
};

const serializeTextRange = (
  formValues: FilterPayloadValues<FilterCommonTypes>,
  menuValues: { [key: string]: FilterMenuValues },
  filter: Filter,
  result: FilterSearchModel,
  filterValidation: FilterValidation[]): { [key: string]: RangeObject } => {

  const rootId = getFieldId(filter.id, fieldType.root);
  const rootHasValues = !!formValues[rootId];
  const range = rootHasValues && formValues[rootId][0];
  const validationResult = filterValidation && filterValidation.find(validation => validation.id === rootId);

  if (!range || validationResult && validationResult.validation && validationResult.validation.level === ValidationLevel.error) {
    return result;
  }

  const serializedRange: any = serializeSingleValue(range, menuValues && menuValues[rootId]);

  return !!serializedRange
    ? {
      ...result,
      [filter.id]: {
        ...serializeApplyRoleAccess(filter, formValues),
        ...serializedRange
      }
    }
    : result;
};

const serializeDateRange = (
  formValues: FilterPayloadValues<FilterCommonTypes>,
  menuValues: { [key: string]: FilterMenuValues },
  filter: Filter,
  result: FilterSearchModel): { [key: string]: DateRangeObject } => {

  const fieldId = getFieldId(filter.id, fieldType.date);
  const range = formValues[fieldId];

  if (!range) {
    return result;
  }

  const serializedRange = serializeDateRangeValue(range, menuValues && menuValues[fieldId], filter);

  return (!!serializedRange && Object.keys(serializedRange).length > 0)
    ? {
      ...result,
      [filter.id]: {
        ...serializeApplyRoleAccess(filter, formValues),
        ...serializedRange
      }
    }
    : result;
};

const serializeDecimalRange = (
  formValues: FilterPayloadValues<FilterCommonTypes>,
  menuValues: { [key: string]: FilterMenuValues },
  filter: Filter,
  result: FilterSearchModel): { [key: string]: DecimalRangeObject } => {

  const fieldId = getFieldId(filter.id, fieldType.number);
  const range = formValues[fieldId];

  if (!range) {
    return result;
  }

  const serializedRange = serializeDecimalRangeValue(range, menuValues && menuValues[fieldId], filter);

  return (!!serializedRange && Object.keys(serializedRange).length > 0)
    ? {
      ...result,
      [filter.id]: {
        ...serializeApplyRoleAccess(filter, formValues),
        ...serializedRange
      }
    }
    : result;
};

const serializeFilter: SerializeFilter = {
  [FilterType.SELECTED_IDS]: serializeSelectedIds,
  [FilterType.TEXT]: serializeText,
  [FilterType.TEXT_RANGE]: serializeTextRange,
  [FilterType.DATE_RANGE]: serializeDateRange,
  [FilterType.DECIMAL_RANGE]: serializeDecimalRange,
  [FilterType.CUSTOM]: serializeCustom,
  [FilterType.DEFAULT_VALUE]: serializeDefaultValue
};

export const serializeCodes = (
  formValues: FilterPayloadValues<FilterCommonTypes>,
  menuValues: { [key: string]: FilterMenuValues },
  filters: Filter[],
  filterValidation: FilterValidation[]
): SerializeCodes => filters && formValues
    ? filters.reduce((result, filter) => serializeFilter[filter.type](formValues, menuValues, filter, result, filterValidation), {})
    : {};
